import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import { StyledTypography } from './styles'; // Assuming you have this
import { Palette } from 'Palette';
import React from 'react';
import LinkifiedHtml from '../LinkifiedHtml';
import sanitizeHTML from '../../services/sanitizeHTML';

// ----------------------------
// FootNoteContainer
// ----------------------------

interface FootNoteContainerProps {
  tone?: keyof typeof Palette;
  className?: string;
}

const FootNoteContainerRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'tone',
})<FootNoteContainerProps>(({ theme, tone = 'default' }) => ({
  marginTop: theme.spacing(3),
  paddingTop: theme.spacing(2),
  borderTop: `1px solid ${Palette[tone].border || '#e0e0e0'}`,
}));

export const FootNoteContainer: React.FC<
  FootNoteContainerProps & React.ComponentProps<typeof Box>
> = ({ className, ...props }) => (
  <FootNoteContainerRoot {...props} className={clsx('react-FootNoteContainer', className)} />
);

// ----------------------------
// FootNote
// ----------------------------

interface FootNoteProps {
  tone?: keyof typeof Palette;
  className?: string;
}

const FootNoteRoot = styled(StyledTypography, {
  shouldForwardProp: (prop) => prop !== 'tone',
})<FootNoteProps>(({ theme, tone = 'default' }) => ({
  fontSize: '0.875rem',
  lineHeight: 1.5,
  marginBottom: theme.spacing(1),
  color: Palette[tone].text,
  '&:last-child': {
    marginBottom: 0,
  },
}));

export const FootNote: React.FC<FootNoteProps & React.ComponentProps<typeof StyledTypography>> = ({
  className,
  ...props
}) => <FootNoteRoot {...props} className={clsx('react-FootNote', className)} />;

// ----------------------------
// FootNoteReference
// ----------------------------

const FootNoteReferenceRoot = styled('sup', {
  shouldForwardProp: (prop) => prop !== 'tone',
})<FootNoteProps>(({ tone = 'default' }) => ({
  fontSize: '0.75rem',
  color: Palette[tone].text,
  cursor: 'pointer',
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

export const FootNoteReference: React.FC<FootNoteProps & React.ComponentProps<'sup'>> = ({
  className,
  ...props
}) => <FootNoteReferenceRoot {...props} className={clsx('react-FootNoteReference', className)} />;

export const FootNoteList = styled('ol', {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone?: keyof typeof Palette }>(({ theme, tone = 'default' }) => ({
  paddingLeft: theme.spacing(3), // or align how you like
  margin: 0,
  color: Palette[tone].text,
  fontSize: '0.875rem',
  lineHeight: 1.5,
  '& li': {
    marginBottom: theme.spacing(1),
    '&:last-child': { marginBottom: 0 },
  },
}));

interface FootNoteItemProps {
  number: number;
  content: string;
  tone?: keyof typeof Palette;
  id?: string;
}

export const FootNoteItem: React.FC<FootNoteItemProps> = ({ number, content, tone, id }) => (
  <FootNote tone={tone} id={id} className="react-FootNoteItem">
    <span
      style={{
        display: 'inline-block',
        minWidth: '2ch',
        textAlign: 'right',
        marginRight: '0.5ch',
      }}
    >
      <strong>{number}.</strong>
    </span>
    <LinkifiedHtml as="span" html={sanitizeHTML(content)} />
  </FootNote>
);
